import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Sun, Music, TreePine } from 'lucide-react';
import { ItineraryModal } from './ItineraryModal';
const destinations = [{
  id: 'bali',
  name: 'Bali',
  theme: 'Healing • Luxury • Reflection',
  description: 'A sanctuary for the soul. Expect breathtaking beach sunsets, deep wellness experiences, and pure luxury as we reflect on the journey so far.',
  color: 'from-emerald/80 to-ocean/80',
  accent: 'text-emerald',
  icon: <Sun className="w-6 h-6" data-id="element-66" />,
  image: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?q=80&w=2038&auto=format&fit=crop'
}, {
  id: 'brazil',
  name: 'Brazil',
  theme: 'Rhythm • Energy • Culture',
  description: 'The ultimate celebration. We are bringing the Carnival spirit with vibrant rhythms, rich Afro-Brazilian culture, and unmatched energy.',
  color: 'from-fuchsia/80 to-sunset/80',
  accent: 'text-fuchsia',
  icon: <Music className="w-6 h-6" data-id="element-67" />,
  image: 'https://images.unsplash.com/photo-1483729558449-99ef09a8c325?q=80&w=2070&auto=format&fit=crop'
}, {
  id: 'botswana',
  name: 'Botswana',
  theme: 'Safari • Nature • Legacy',
  description: 'Returning to the roots. An adventurous safari experience surrounded by raw African beauty, honoring legacy and the wild unknown.',
  color: 'from-sunset/80 to-gold/80',
  accent: 'text-sunset',
  icon: <TreePine className="w-6 h-6" data-id="element-68" />,
  image: 'https://images.unsplash.com/photo-1621508654686-809f23efdabc?q=80&w=2070&auto=format&fit=crop'
}];
export function Destinations() {
  const [activeItinerary, setActiveItinerary] = useState<'bali' | 'brazil' | 'botswana' | null>(null);
  return <section className="py-32 bg-rich-black relative" data-id="element-69">
      <ItineraryModal isOpen={!!activeItinerary} onClose={() => setActiveItinerary(null)} destinationId={activeItinerary} data-id="element-70" />
      <div className="container mx-auto px-6" data-id="element-71">
        <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-72">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="font-display text-4xl md:text-5xl font-bold mb-6" data-id="element-73">
            Why the <span className="text-gradient-sunset" data-id="element-74">Three B's?</span>
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="font-sans text-lg text-gray-300" data-id="element-75">
            "After visiting over 60 countries, I wanted my 40th birthday to feel
            less like a party... and more like an experience."
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-76">
          {destinations.map((dest, index) => <motion.div key={dest.id} initial={{
          opacity: 0,
          y: 50
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: index * 0.2
        }} className="group relative h-[600px] rounded-3xl overflow-hidden cursor-pointer" data-id="element-77">
              {/* Background Image */}
              <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110" style={{
            backgroundImage: `url(${dest.image})`
          }} data-id="element-78" />

              {/* Gradient Overlay */}
              <div className={`absolute inset-0 bg-gradient-to-t ${dest.color} mix-blend-multiply opacity-60 transition-opacity duration-500 group-hover:opacity-80`} data-id="element-79" />
              <div className="absolute inset-0 bg-gradient-to-t from-rich-black via-rich-black/50 to-transparent opacity-90" data-id="element-80" />

              {/* Content */}
              <div className="absolute inset-0 p-8 flex flex-col justify-end" data-id="element-81">
                <div className="transform transition-transform duration-500 translate-y-8 group-hover:translate-y-0" data-id="element-82">
                  <div className={`w-12 h-12 rounded-full glass-panel flex items-center justify-center mb-6 ${dest.accent}`} data-id="element-83">
                    {dest.icon}
                  </div>

                  <h3 className="font-display text-4xl font-bold text-white mb-2 flex items-center gap-2" data-id="element-84">
                    {dest.name}
                  </h3>

                  <p className="font-sans text-sm font-semibold tracking-widest text-gold uppercase mb-4" data-id="element-85">
                    {dest.theme}
                  </p>

                  <div className="opacity-0 transition-opacity duration-500 group-hover:opacity-100" data-id="element-86">
                    <p className="font-sans text-gray-300 leading-relaxed mb-6" data-id="element-87">
                      {dest.description}
                    </p>
                    <button onClick={() => setActiveItinerary(dest.id as 'bali' | 'brazil' | 'botswana')} className="flex items-center gap-2 text-white font-sans text-sm font-semibold hover:text-gold transition-colors" data-id="element-88">
                      <MapPin size={16} data-id="element-89" /> Explore Itinerary
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}